import i18next from "i18next";
import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import {
  isNavUnavailableError,
  isNavUnavailableRetrySafe,
  NAV_UNAVAILABLE_RETRYING_KEY,
} from "../../utils/functions/navUnavailable";
import { showInfoToast } from "../../presentation/components/Toast/Toast";

// 503 navUnavailable handling on place/commit order (flag
// REACT_APP_NAV_UNAVAILABLE_ENABLED, NAV-ROBUSTHED-2026-09-29.md §7.2) -
// the sibling of productPendingRetry.ts.
//
// The API's entry check (OrderEntryLatchFailFastEnabled) refuses PlaceOrder
// / the delivery-date preview BEFORE its first NAV call and before anything
// is written while its NAV health latch is open: 503 { code:
// "navUnavailable", reason: "latchOpen", retrySafe: true, retryAfterSeconds }
// (0 NAV calls, no order number drawn). authFetch turns that into a
// DataError whose errorStatus carries the HTTP status (503); under the flag
// errorStatusFromBody keeps the marker as errorCode, retryAfterSeconds,
// reason and retrySafe.
//
// Policy (owner): ONLY when the API said retrySafe === true - one info
// toast ("The item system is restarting - retrying in N s…") + ONE
// automatic retry after retryAfterSeconds, clamped 2-15 s. A 503 with
// retrySafe false (NAV was lost INSIDE the request - an order number may
// already have been drawn), a second 503 or any other error is rethrown
// untouched so the caller's withToasts shows it as today (one toast).
// Flag off = `run()` is returned as is: no catch, no toast, no retry.

export const NAV_UNAVAILABLE_RETRY_DEFAULT_DELAY_MS = 5_000;
export const NAV_UNAVAILABLE_RETRY_MIN_DELAY_MS = 2_000;
export const NAV_UNAVAILABLE_RETRY_MAX_DELAY_MS = 15_000;

function clamp(ms: number): number {
  return Math.min(
    NAV_UNAVAILABLE_RETRY_MAX_DELAY_MS,
    Math.max(NAV_UNAVAILABLE_RETRY_MIN_DELAY_MS, Math.round(ms))
  );
}

// retryAfterSeconds from the 503 envelope - carried on the ErrorStatus by
// errorStatusFromBody; otherwise the default. Clamped so a bad value can
// neither hammer the API nor park the click for minutes (the API's default
// NavRetryAfterSeconds is 30 - the user gets the 15 s cap, then one try).
export function navUnavailableRetryDelayMs(error: unknown): number {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const carried = Number(status?.retryAfterSeconds);
  if (Number.isFinite(carried) && carried > 0) return clamp(carried * 1000);
  return clamp(NAV_UNAVAILABLE_RETRY_DEFAULT_DELAY_MS);
}

function defaultNotify(delayMs: number): void {
  showInfoToast(
    i18next.t(NAV_UNAVAILABLE_RETRYING_KEY, {
      seconds: Math.round(delayMs / 1000),
    }),
    delayMs
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface NavUnavailableRetryOptions {
  // Replaces the info toast (tests, silent callers).
  notify?: (delayMs: number) => void;
}

export async function withNavUnavailableRetry<T>(
  run: () => Promise<T>,
  options?: NavUnavailableRetryOptions
): Promise<T> {
  if (!enviroment.navUnavailableEnabled) return run();
  try {
    return await run();
  } catch (error) {
    if (!isNavUnavailableError(error)) throw error;
    if (!isNavUnavailableRetrySafe(error)) throw error;
    const delayMs = navUnavailableRetryDelayMs(error);
    if (options?.notify) options.notify(delayMs);
    else defaultNotify(delayMs);
    await wait(delayMs);
    return run();
  }
}
